import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtDate, CATEGORY_LABELS, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { TriState } from "@/pages/ReceiptDetail";
import { ReceiptLabelDialog } from "@/components/ReceiptLabelDialog";
import { Plus, Search, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const CATS = Object.entries(CATEGORY_LABELS).map(([k, v]) => ({ k, v }));

function QuickChecklist({ groups, results, setResults, notes, setNotes }) {
  const hasFail = Object.values(results).some((v) => v === "tidak_memenuhi");
  return (
    <div className="space-y-3 border-t pt-4" data-testid="rf-checklist">
      <h4 className="font-heading font-bold text-sm">Checklist Pemeriksaan MJ-FORM-01</h4>
      <p className="text-xs text-muted-foreground">Centang kriteria yang perlu saja (minimal satu). Yang tidak dicentang tercatat kosong ("-").</p>
      {groups.map((g) => (
        <div key={g.key} className="space-y-2">
          <div className="text-xs font-bold text-muted-foreground">{g.label}</div>
          {g.items.map((it) => (
            <div key={it.key} className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm">{it.label}</span>
              <TriState testid={`rf-qc-${it.key}`} value={results[it.key]} onChange={(v) => setResults({ ...results, [it.key]: results[it.key] === v ? "" : v })} />
            </div>
          ))}
        </div>
      ))}
      {hasFail && <div data-testid="rf-fail-warning" className="flex gap-2 p-2 rounded bg-red-50 border border-red-200 text-red-700 text-xs"><AlertTriangle className="w-4 h-4" /> Ada kriteria TIDAK MEMENUHI: bahan akan masuk KARANTINA dan belum bisa dipakai produksi.</div>}
      <div><Label className="text-xs">Catatan Pemeriksaan</Label><Textarea data-testid="rf-qc-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" /></div>
    </div>
  );
}

export function ReceiptForm({ open, onClose, onSaved }) {
  const { user, meta } = useAuth();
  const groups = meta?.criteria_groups || [];
  const [results, setResults] = useState({});
  const [notes, setNotes] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [units, setUnits] = useState([]);
  const [locations, setLocations] = useState([]);
  const [f, setF] = useState(init());
  const [saving, setSaving] = useState(false);

  function init() {
    return { tgl_terima: new Date().toISOString().slice(0, 10), supplier_id: "", material_id: "",
      category: "", supplier_batch: "", jumlah: "", unit: "", tgl_produksi: "", expiry_date: "",
      surat_jalan: "", location: "", penerima: user?.name || "" };
  }

  useEffect(() => {
    if (!open) return;
    setF(init()); setResults({}); setNotes("");
    Promise.all([api.get("/suppliers"), api.get("/materials"), api.get("/units"), api.get("/locations")])
      .then(([s, m, u, l]) => { setSuppliers(s.data); setMaterials(m.data); setUnits(u.data); setLocations(l.data); });
  }, [open]);

  function pickMaterial(id) {
    const m = materials.find((x) => x.id === id);
    setF((p) => ({ ...p, material_id: id, category: m?.category || p.category, unit: m?.default_unit || p.unit }));
  }

  async function save(submit) {
    if (!f.supplier_id || !f.material_id || !f.category || !f.jumlah || !f.unit) {
      toast.error("Lengkapi pemasok, bahan, kategori, jumlah, dan satuan."); return;
    }
    const res = Object.fromEntries(Object.entries(results).filter(([, v]) => v));
    if (submit && !Object.keys(res).length) { toast.error("Isi minimal satu kriteria checklist."); return; }
    setSaving(true);
    try {
      const payload = { ...f, jumlah: parseFloat(f.jumlah), submit, inspection: submit ? { results: res, notes } : null };
      const { data } = await api.post("/receipts", payload);
      if (data.warning) toast.warning(data.warning);
      toast.success(data.status === "DITERIMA" ? `Diterima & stok bertambah — Batch ${data.internal_batch}` : `Penerimaan tersimpan — Batch ${data.internal_batch}`);
      onSaved?.(data); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">Penerimaan Bahan Baru</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Tanggal Penerimaan *"><Input type="date" data-testid="rf-tgl" value={f.tgl_terima} onChange={(e) => setF({ ...f, tgl_terima: e.target.value })} /></Field>
          <Field label="Pemasok *">
            <Select value={f.supplier_id} onValueChange={(v) => setF({ ...f, supplier_id: v })}>
              <SelectTrigger data-testid="rf-supplier"><SelectValue placeholder="Pilih pemasok" /></SelectTrigger>
              <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.code} — {s.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Nama Bahan *">
            <Select value={f.material_id} onValueChange={pickMaterial}>
              <SelectTrigger data-testid="rf-material"><SelectValue placeholder="Pilih bahan" /></SelectTrigger>
              <SelectContent>{materials.map((m) => <SelectItem key={m.id} value={m.id}>{m.code} — {m.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Kategori Bahan *">
            <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
              <SelectTrigger data-testid="rf-category"><SelectValue placeholder="Pilih kategori" /></SelectTrigger>
              <SelectContent>{CATS.map((c) => <SelectItem key={c.k} value={c.k}>{c.v}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Nomor Batch Pemasok"><Input data-testid="rf-supbatch" value={f.supplier_batch} onChange={(e) => setF({ ...f, supplier_batch: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Jumlah *"><Input type="number" step="any" data-testid="rf-jumlah" value={f.jumlah} onChange={(e) => setF({ ...f, jumlah: e.target.value })} /></Field>
            <Field label="Satuan *">
              <Select value={f.unit} onValueChange={(v) => setF({ ...f, unit: v })}>
                <SelectTrigger data-testid="rf-unit"><SelectValue placeholder="Satuan" /></SelectTrigger>
                <SelectContent>{units.map((u) => <SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Tanggal Produksi"><Input type="date" data-testid="rf-prod" value={f.tgl_produksi} onChange={(e) => setF({ ...f, tgl_produksi: e.target.value })} /></Field>
          <Field label="Tanggal Kedaluwarsa"><Input type="date" data-testid="rf-exp" value={f.expiry_date} onChange={(e) => setF({ ...f, expiry_date: e.target.value })} /></Field>
          <Field label="No. Surat Jalan"><Input data-testid="rf-sj" value={f.surat_jalan} onChange={(e) => setF({ ...f, surat_jalan: e.target.value })} /></Field>
          <Field label="Lokasi Penyimpanan">
            <Select value={f.location} onValueChange={(v) => setF({ ...f, location: v })}>
              <SelectTrigger data-testid="rf-loc"><SelectValue placeholder="Pilih lokasi" /></SelectTrigger>
              <SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Petugas Penerima"><Input data-testid="rf-penerima" value={f.penerima} onChange={(e) => setF({ ...f, penerima: e.target.value })} /></Field>
        </div>
        <QuickChecklist groups={groups} results={results} setResults={setResults} notes={notes} setNotes={setNotes} />
        <DialogFooter className="gap-2">
          <Button variant="outline" data-testid="rf-save-draft" disabled={saving} onClick={() => save(false)}>Simpan Draft</Button>
          <Button data-testid="rf-submit" disabled={saving} onClick={() => save(true)}>Simpan &amp; Terima ke Stok</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return <div><Label className="text-xs">{label}</Label><div className="mt-1">{children}</div></div>;
}

export default function Receipts() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const [printR, setPrintR] = useState(null);

  async function load() {
    const params = {};
    if (q) params.q = q;
    if (status !== "all") params.status = status;
    const { data } = await api.get("/receipts", { params });
    setRows(data);
  }
  useEffect(() => { load(); }, [status]);

  const canCreate = ["gudang", "qc", "manajer", "admin"].includes(user?.role);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div><h1 className="font-heading text-2xl font-extrabold">Penerimaan Barang</h1>
          <p className="text-sm text-muted-foreground">Catat dan kelola penerimaan bahan baku &amp; kemasan.</p></div>
        {canCreate && <Button data-testid="new-receipt-btn" onClick={() => setOpen(true)}><Plus className="w-4 h-4 mr-1" /> Penerimaan Baru</Button>}
      </div>

      <Card className="p-4">
        <div className="flex flex-wrap gap-3 mb-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="receipt-search" placeholder="Cari batch / bahan..." value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="pl-9" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-[220px]" data-testid="receipt-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Status</SelectItem>
              {["DRAFT","MENUNGGU_PEMERIKSAAN","KARANTINA","DITERIMA","DITOLAK","DIBATALKAN"].map((s) => <SelectItem key={s} value={s}>{s.replace("_"," ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} data-testid="receipt-apply">Terapkan</Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-muted-foreground border-b">
              <th className="py-2 font-medium">Batch Internal</th><th className="font-medium">Tgl Terima</th>
              <th className="font-medium">Bahan</th><th className="font-medium">Kategori</th>
              <th className="font-medium">Pemasok</th><th className="font-medium">Jumlah</th><th className="font-medium">Status</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`receipt-row-${r.internal_batch}`} className="border-b border-slate-100 dark:border-slate-800 hover:bg-accent/50 cursor-pointer" onClick={() => nav(`/receipts/${r.id}`)}>
                  <td className="py-2.5"><span className="batch-chip">{r.internal_batch}</span></td>
                  <td>{fmtDate(r.tgl_terima)}</td><td>{r.material_name}</td>
                  <td className="text-xs">{CATEGORY_LABELS[r.category]}</td>
                  <td>{r.supplier_name}</td><td>{r.jumlah} {r.unit}</td><td><StatusBadge status={r.status} /></td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">Tidak ada data penerimaan.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
      <ReceiptForm open={open} onClose={() => setOpen(false)} onSaved={(d) => { load(); if (["DITERIMA", "KARANTINA"].includes(d?.status)) setPrintR(d); }} />
      <ReceiptLabelDialog receipt={printR} onClose={() => setPrintR(null)} />
    </div>
  );
}
